// React Dependencies
import React from 'react';

// External Dependencies
import PropTypes from 'prop-types';
import moment from 'moment';
import { makeStyles } from '@material-ui/core';

// Internal Dependencies
import MText from '../../../../../components/Common/MText';
import Colours from '../../../../../styles/colours';
import ClaimButton from './ClaimButton';
import withLoyaltyConnect from '../WithLoyaltyConnect';
import ProgressiveImage from '../../../../../components/ProgressiveImage';
import { StandardLCTnC } from '../../../../../common/constants';
import { IsEmpty } from '../../../../../common/checks';
import { CalculateExpiryDateInSGT, ConvertDateToSGTime } from '../../../../../common/util';
import { DesignTypeOptions } from '../constants';

const useStyles = makeStyles({
  loyaltyCardName: {
    lineHeight:                  1,
    display:                     '-webkit-box',
    WebkitBoxOrient:             'vertical',
    WebkitLineClamp:             2,
    overflow:                    'hidden',
    textOverflow:                'ellipsis',
    whiteSpace:                  'pre-line',
    wordBreak:                   'break-word',
    width:                       '100%',
    '@media (min-width: 769px)': {
      fontSize: '8px !important',
    },
    '@media (min-width: 850px)': {
      fontSize: '11px !important',
    },
    '@media (min-width: 1300px)': {
      fontSize: '13px !important',
    },
  },
  loyaltyCardContainer: {
    padding:                     16,
    top:                         0,
    '@media (min-width: 820px)': {
      padding: 10,
    },
    '@media (min-width: 992px)': {
      padding: 12,
    },
    '@media (min-width: 1200px)': {
      padding: 16,
    },
  },
  tncContent: {
    lineHeight:                  1,
    display:                     '-webkit-box',
    WebkitBoxOrient:             'vertical',
    WebkitLineClamp:             6,
    overflow:                    'hidden',
    textOverflow:                'ellipsis',
    margin:                      0, // Remove default pre margins
    width:                       '100%', // Ensure it takes full width
    '@media (min-width: 820px)': {
      WebkitLineClamp: 4,
    },
    '@media (min-width: 1300px)': {
      WebkitLineClamp: 8,
    },
  },
  expiryDateText: {
    textAlign:                   'center',
    '@media (min-width: 769px)': {
      fontSize: '5px !important',
    },
    '@media (min-width: 800px)': {
      fontSize: '6px !important',
    },
    '@media (min-width: 1200px)': {
      fontSize: '9.6px !important',
    },
  },
  stampHolder: {
    paddingLeft:                 16,
    paddingRight:                16,
    '@media (min-width: 769px)': {
      paddingLeft:  8,
      paddingRight: 8,
    },
    '@media (min-width: 850px)': {
      paddingLeft:  12,
      paddingRight: 12,
    },
    '@media (min-width: 1400px)': {
      paddingLeft:  16,
      paddingRight: 16,
    },
  },
  stampHolderContainer: {
    margin:                      '40px 0px', // shorthand for (margin top and bottom) and (margin left and right)
    '@media (min-width: 769px)': {
      margin: '16px 0px',
    },
    '@media (min-width: 800px)': {
      margin: '20px 0px',
    },
    '@media (min-width: 1024px)': {
      margin: '28px 0px',
    },
    '@media (min-width: 1200px)': {
      margin: '32px 0px',
    },
  },
});

const stampsImage = Array.from({ length: 10 }, (_, i) => `/images/stamp-holders/stamp-${i + 1}.png`);

const PreviewLoyaltyCard = (props) => {
  const classes = useStyles();

  const {
    uploadedDesign, selectedLoyaltyCardTemplate, selectedStampsHolderTemplate,
    stampsHolderBackgroundColour, designType,
  } = props.loyaltyProgram.design;
  const { numOfStamps } = props.loyaltyProgram.stamp;
  const { vouchers } = props.loyaltyProgram;

  const findFirstValidVoucher = (vouchersList) => {
    for (let i = 0; i < vouchersList.length; i += 1) {
      const vc = vouchersList[i];
      const isLast = i === vouchersList.length - 1;

      if (vc.issuableQty == null || vc.balQty > 0 || isLast) {
        return vc;
      }
    }

    return null; // if no voucher matched, which technically shouldn't happen due to last voucher fallback
  };

  const selectedVoucher = IsEmpty(vouchers)
    ? null
    : findFirstValidVoucher(vouchers);

  const getLoyaltyCardExpiryDate = () => {
    const isExpiryDateOrValidityNotSet = IsEmpty(props.loyaltyProgram.expiryDate)
      && IsEmpty(props.loyaltyProgram.validityInDays)
      && IsEmpty(props.loyaltyProgram.validityInMonths);

    if (isExpiryDateOrValidityNotSet) {
      return '-';
    } else if (!IsEmpty(props.loyaltyProgram.expiryDate)) {
      return moment(props.loyaltyProgram.expiryDate).format('DD MMM YYYY');
    } else {
      const currentDate = ConvertDateToSGTime(new Date());
      const expiryDate = CalculateExpiryDateInSGT(currentDate, props.loyaltyProgram.validityInMonths || 0, props.loyaltyProgram.validityInDays || 0);
      return moment(expiryDate).utcOffset('+08:00').format('DD MMM YYYY');
    }
  };

  const getChunkedStamps = (count) => {
    const result = [];
    const stampsToRender = stampsImage.slice(0, count);
    for (let i = 0; i < stampsToRender.length; i += 3) {
      result.push(stampsToRender.slice(i, i + 3));
    }
    return result;
  };

  const rows = IsEmpty(numOfStamps) || numOfStamps <= 0 ? [] : getChunkedStamps(numOfStamps);
  const lastRow = rows[rows.length - 1] || [];

  const showButtonInSameRow = lastRow.length === 1;
  const showButtonInNewRow = lastRow.length >= 2;

  return (
    <div className={props.containerClassname}>
      <MText text="Card Preview" className="text-center pb-1 pb-lg-3" style={{ fontWeight: 500 }} size={16} />
      <div>
        <div
          className="w-100 position-relative overflow-hidden"
          style={{ paddingTop: 'calc((46 / 80) * 100%)' }}
        >
          {
            (!IsEmpty(uploadedDesign) || !IsEmpty(selectedLoyaltyCardTemplate)) && (
              <>
                <ProgressiveImage
                  image={designType === DesignTypeOptions.Manual && !IsEmpty(uploadedDesign)
                    ? uploadedDesign
                    : selectedLoyaltyCardTemplate}
                  alt="loyalty-card-background"
                  style={{
                    top:          0,
                    bottom:       0,
                    height:       '100%',
                    width:        '100%',
                    objectFit:    'cover',
                    position:     'absolute',
                    borderRadius: 8,
                  }}
                />
                <div className={`w-100 h-100 position-absolute d-flex flex-column ${classes.loyaltyCardContainer}`}>
                  <div
                    className="row mx-0"
                    style={{
                      flex:      '0 0 33.33%',
                      height:    '33.33%',
                      minHeight: 0,
                    }}
                  >
                    <div className="col-4 px-0 h-100">
                      <div className="h-100" style={{ aspectRatio: 1 }}>
                        <img
                          src={props.page.pageLogo}
                          alt="merchant-logo"
                          className="rounded-circle"
                          style={{
                            height:         '100%',
                            objectFit:      'cover',
                            objectPosition: 'center',
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div
                    className="row mx-0 overflow-hidden"
                    style={{
                      flex:      '0 0 33.33%',
                      height:    '33.33%',
                      minHeight: 0,
                    }}
                  >
                    <div className="col-7 px-0 h-100">
                      <MText
                        text={props.loyaltyProgram.name}
                        colour={Colours.White}
                        className={classes.loyaltyCardName}
                        size={14}
                        bold
                      />
                    </div>
                  </div>
                  <div
                    className="row mx-0"
                    style={{
                      flex:      '0 0 33.33%',
                      height:    '33.33%',
                      minHeight: 0,
                    }}
                  >
                    <div className="col-10 pl-0 pr-1 h-100">
                      <MText
                        text="TERMS & CONDITIONS"
                        size={4}
                        bold
                        colour={Colours.White}
                      />
                      <MText
                        text={`${StandardLCTnC} ${!IsEmpty(props.loyaltyProgram.additionalTnC) ? props.loyaltyProgram.additionalTnC : ''}`}
                        size={4}
                        colour={Colours.White}
                        className={classes.tncContent}
                      />
                    </div>
                    <div className="col-2 h-100 px-0">
                      <img alt="camera" src="/images/loyalty-card-camera.png" />
                    </div>
                  </div>
                </div>
              </>
            )
          }
        </div>
        <div className="w-100 overflow-hidden mt-3 position-relative">
          <div
            style={{
              paddingTop: 'calc((702/472) * 100%)', // Based on the template, the stamps holder background resolution is 472 x 702
              width:      '100%',
              height:     '100%',
            }}
          />
          {
            (!IsEmpty(uploadedDesign) || !IsEmpty(selectedLoyaltyCardTemplate)) ? (
              <div className="position-absolute w-100 h-100" style={{ top: 0, left: 0 }}>
                <div className="position-relative w-100 h-100">
                  {
                    (!IsEmpty(uploadedDesign) && designType === DesignTypeOptions.Manual) ? (
                      <div
                        className="w-100 h-100"
                        style={{
                          backgroundColor: !IsEmpty(stampsHolderBackgroundColour) ? stampsHolderBackgroundColour : Colours.Gray7,
                          borderRadius:    12,
                        }}
                      />
                    ) : (
                      <img
                        src={selectedStampsHolderTemplate}
                        alt="stamps-holder-background"
                        className="w-100"
                      />
                    )
                  }
                  <div className="w-100 h-100 py-2 position-absolute" style={{ top: 0 }}>
                    <MText
                      className={classes.expiryDateText}
                      colour={Colours.White}
                      size={9}
                      text={`Expiry Date: ${getLoyaltyCardExpiryDate()}`}
                    />
                    <div className={classes.stampHolderContainer}>
                      {
                          rows.map((row, rowIdx) => {
                            const isLastRow = rowIdx === rows.length - 1;
                            const isFirstRow = rowIdx === 0;

                            return (
                              <div key={rowIdx} className={`row mx-0${!isFirstRow ? ' mb-1 mt-2 mt-lg-3' : ''}`}>
                                {
                                  row.map((stamp, colIdx) => (
                                    <div className={`col-4 ${classes.stampHolder}`} key={colIdx}>
                                      <img className="w-100 h-auto" src={stamp} alt="stamp" />
                                    </div>
                                  ))
                                }
                                {isLastRow && showButtonInSameRow && (
                                  <ClaimButton
                                    disabled
                                    isProduct={selectedVoucher?.isProduct}
                                    label={selectedVoucher?.label}
                                  />
                                )}
                              </div>
                            );
                          })
                      }
                      {
                        showButtonInNewRow && (
                          <div className="row mx-0 mb-1 mt-2 mt-lg-3">
                            <div className="col-4" />
                            <ClaimButton
                              disabled
                              isProduct={selectedVoucher?.isProduct}
                              label={selectedVoucher?.label}
                            />
                          </div>
                        )
                      }
                    </div>
                  </div>
                </div>
              </div>
            ) : null
          }
        </div>
      </div>
    </div>
  );
};

PreviewLoyaltyCard.defaultProps = {
  containerClassname: '',
};

PreviewLoyaltyCard.propTypes = {
  page: PropTypes.shape({
    pageLogo: PropTypes.string,
  }).isRequired,
  containerClassname: PropTypes.string,
  loyaltyProgram:     PropTypes.shape({
    name:             PropTypes.string,
    expiryDate:       PropTypes.string,
    validityInDays:   PropTypes.number,
    validityInMonths: PropTypes.number,
    stamp:            PropTypes.shape({
      numOfStamps: PropTypes.number,
    }),
    design: PropTypes.shape({
      uploadedDesign:               PropTypes.string,
      selectedLoyaltyCardTemplate:  PropTypes.string,
      selectedStampsHolderTemplate: PropTypes.string,
      stampsHolderBackgroundColour: PropTypes.string,
      designType:                   PropTypes.oneOf(Object.values(DesignTypeOptions)),
    }),
    vouchers:      PropTypes.arrayOf(PropTypes.shape({})),
    additionalTnC: PropTypes.string,
  }).isRequired,
};

export default withLoyaltyConnect(PreviewLoyaltyCard);
